import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { BRAND_MINT } from '@/lib/utils'
import { getActivityFeedApi, type ActivityFeedEvent } from '@/lib/api'
import {
  LoaderIcon,
  ActivityIcon,
  UserPlusIcon,
  ShieldCheckIcon,
  FlagIcon,
  BanknoteIcon,
  FileEditIcon,
  XCircleIcon,
  AlertTriangleIcon,
  ClockIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from 'lucide-react'

function formatTimestamp(iso: string) {
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function formatTimeAgo(iso: string): string {
  const now = Date.now()
  const then = new Date(iso).getTime()
  const diffMs = now - then
  const diffMin = Math.floor(diffMs / 60000)
  const diffHrs = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMin < 1) return 'Just now'
  if (diffMin < 60) return `${diffMin}m ago`
  if (diffHrs < 24) return `${diffHrs}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return formatTimestamp(iso)
}

const EVENT_ICONS: Record<string, React.ElementType> = {
  audit: ShieldCheckIcon,
  user_registered: UserPlusIcon,
  circle_created: BanknoteIcon,
}

const EVENT_COLORS: Record<string, string> = {
  audit: 'text-blue-500',
  user_registered: 'text-emerald-500',
  circle_created: 'text-amber-500',
}

const ACTION_LABELS: Record<string, string> = {
  'org.approve': 'Org approved',
  'org.reject': 'Org rejected',
  'org.update': 'Org updated',
  'org.verify_override': 'Verification override',
  'user.create': 'User created',
  'user.suspend': 'User suspended',
  'user.unsuspend': 'User reactivated',
  'user.update': 'User updated',
  'user.delete': 'User anonymized',
  'session.revoke': 'Session revoked',
  'request.cancel': 'Circle cancelled',
  'cycle.force_pay': 'Cycle force-paid',
  'settings.update': 'Settings updated',
  'feature_flag.update': 'Flag updated',
  'feature_flag.delete': 'Flag deleted',
  'email.manual_send': 'Email sent (manual)',
  'push.manual_send': 'Push sent (manual)',
  'org.transfer_ownership': 'Ownership transferred',
  'employee_added': 'Employee added',
  'self_registered': 'User registered',
  'circle.created': 'Circle created',
}

function getEventIcon(event: ActivityFeedEvent) {
  return EVENT_ICONS[event.event_type] || ActivityIcon
}

function getEventColor(event: ActivityFeedEvent) {
  return EVENT_COLORS[event.event_type] || 'text-muted-foreground'
}

function getActionLabel(action: string): string {
  return ACTION_LABELS[action] || action.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

export const Route = createFileRoute('/dashboard/admin/activity-feed')({
  component: () => {
    const [events, setEvents] = useState<ActivityFeedEvent[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [limit] = useState(20)
    const [loading, setLoading] = useState(true)

    const totalPages = Math.max(1, Math.ceil(total / limit))

    useEffect(() => {
      let cancelled = false
      async function load() {
        setLoading(true)
        try {
          const res = await getActivityFeedApi({ page, limit })
          if (!cancelled) {
            setEvents(res.data)
            setTotal(res.total)
          }
        } catch (err) {
          if (!cancelled) {
            toast.error(
              err instanceof Error ? err.message : 'Failed to load activity feed',
            )
          }
        } finally {
          if (!cancelled) setLoading(false)
        }
      }
      load()
      return () => { cancelled = true }
    }, [page, limit])

    if (loading && events.length === 0) {
      return (
        <div className="mx-auto max-w-5xl">
          <div className="mb-8">
            <div className="flex items-start gap-3">
              <span aria-hidden="true" className="mt-0.5 shrink-0" style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }} />
              <h1 className="text-2xl font-semibold">Activity feed</h1>
            </div>
          </div>
          <div className="flex items-center justify-center rounded-xl border border-border bg-card py-16">
            <LoaderIcon className="size-5 animate-spin text-muted-foreground" />
            <p className="ml-2 text-sm text-muted-foreground">Loading activity feed…</p>
          </div>
        </div>
      )
    }

    return (
      <div className="mx-auto max-w-5xl">
        <div className="mb-8">
          <div className="flex items-start gap-3">
            <span aria-hidden="true" className="mt-0.5 shrink-0" style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }} />
            <div>
              <h1 className="text-2xl font-semibold">Activity feed</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Timeline of recent platform-wide events.
              </p>
            </div>
          </div>
        </div>

        {events.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card py-16">
            <ActivityIcon className="size-10 text-muted-foreground/40" />
            <p className="text-sm font-medium text-foreground">No activity yet</p>
            <p className="text-xs text-muted-foreground">Events will appear here as users interact with the platform.</p>
          </div>
        ) : (
          <>
            {/* Timeline */}
            <div className="space-y-1">
              {events.map((event, idx) => {
                const Icon = getEventIcon(event)
                const color = getEventColor(event)
                return (
                  <div key={`${event.event_type}-${event.entity_id}-${event.event_time}-${idx}`}
                    className="flex gap-4 rounded-xl border border-border bg-card px-4 py-3 transition-colors hover:bg-accent/50"
                  >
                    {/* Timeline dot + line */}
                    <div className="flex flex-col items-center gap-1">
                      <div className={`flex size-8 shrink-0 items-center justify-center rounded-full bg-background ${color}`}>
                        <Icon className="size-4" />
                      </div>
                      {idx < events.length - 1 && (
                        <div className="w-px flex-1 bg-border" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="min-w-0 flex-1 pb-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground">
                            {getActionLabel(event.event_action)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {event.actor_name || 'System'}
                            {event.detail && (
                              <span className="ml-1">· {event.detail}</span>
                            )}
                            {event.entity_type !== 'settings' && (
                              <span className="ml-1">· {event.entity_type} #{event.entity_id}</span>
                            )}
                          </p>
                        </div>
                        <span className="shrink-0 text-[11px] text-muted-foreground/60 whitespace-nowrap">
                          {formatTimeAgo(event.event_time)}
                        </span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-center gap-3">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="size-4" />
                  Newer
                </button>
                <span className="text-xs text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Older
                  <ChevronRightIcon className="size-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    )
  },
})
